import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";
import { ChannelBadge, CHANNELS } from "@/components/channels";
import { Shirt, Sparkles, Cpu, Home, Dumbbell, Gem, Target, TrendingUp, Repeat, ShoppingBag, Check } from "lucide-react";

const SECTORS = [
  { id: "Mode", Icon: Shirt }, { id: "Beauté", Icon: Sparkles }, { id: "High-Tech", Icon: Cpu },
  { id: "Maison", Icon: Home }, { id: "Sport", Icon: Dumbbell }, { id: "Luxe", Icon: Gem },
];
const OBJECTIVES = [
  { id: "Acquisition de clients", Icon: Target }, { id: "Conversions Meta", Icon: TrendingUp },
  { id: "ROAS TikTok", Icon: TrendingUp }, { id: "Re-targeting", Icon: Repeat },
];

export default function Onboarding() {
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  const [step, setStep] = useState(0);
  const [data, setData] = useState({ sector: "", objective: "", channels: [], brand_name: user?.name || "", shopify_url: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user?.onboarded) navigate("/dashboard");
  }, [user, navigate]);

  if (user?.onboarded) return null;

  const toggleChannel = (id) =>
    setData((d) => ({ ...d, channels: d.channels.includes(id) ? d.channels.filter((c) => c !== id) : [...d.channels, id] }));

  const next = () => setStep((s) => s + 1);
  const canNext = [data.sector, data.objective, data.channels.length > 0, true][step];

  const finish = async () => {
    setSaving(true);
    try {
      const { data: updated } = await api.post("/auth/onboarding", data);
      setUser(updated);
      toast.success("Configuration terminée ! Bienvenue dans votre studio.");
      navigate("/dashboard");
    } catch {
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const steps = ["Secteur", "Objectif", "Canaux", "Catalogue"];

  return (
    <div className="min-h-screen bg-background mg-radial flex flex-col">
      <div className="px-6 h-16 flex items-center border-b border-border"><Logo /></div>
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-2xl mg-fade-up">
          <div className="flex items-center gap-2 mb-8">
            {steps.map((s, i) => (
              <div key={s} className="flex-1">
                <div className={`h-1.5 rounded-full transition-all ${i <= step ? "bg-gradient-to-r from-indigo-500 to-cyan-500" : "bg-secondary"}`} />
                <p className={`text-xs mt-2 ${i <= step ? "text-foreground font-medium" : "text-muted-foreground"}`}>{s}</p>
              </div>
            ))}
          </div>

          {step === 0 && (
            <div data-testid="onboarding-step-sector">
              <h2 className="font-display text-3xl font-bold mb-2">Quel est votre secteur ?</h2>
              <p className="text-muted-foreground mb-6">Nous adapterons l'IA à votre marché e-commerce.</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {SECTORS.map(({ id, Icon }) => (
                  <button key={id} onClick={() => setData({ ...data, sector: id })}
                    className={`p-5 rounded-2xl border text-left transition-all ${data.sector === id ? "border-primary bg-primary/10 mg-glow" : "border-border hover:border-primary/40 bg-card"}`}
                    data-testid={`onboarding-sector-${id}`}>
                    <Icon size={24} className="text-primary mb-3" />
                    <p className="font-semibold">{id}</p>
                  </button>
                ))}
              </div>
            </div>
          )}

          {step === 1 && (
            <div data-testid="onboarding-step-objective">
              <h2 className="font-display text-3xl font-bold mb-2">Votre objectif principal ?</h2>
              <p className="text-muted-foreground mb-6">L'IA optimisera vos créations pour ce but.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {OBJECTIVES.map(({ id, Icon }) => (
                  <button key={id} onClick={() => setData({ ...data, objective: id })}
                    className={`p-5 rounded-2xl border flex items-center gap-3 transition-all ${data.objective === id ? "border-primary bg-primary/10 mg-glow" : "border-border hover:border-primary/40 bg-card"}`}
                    data-testid={`onboarding-objective-${id}`}>
                    <Icon size={22} className="text-cyan-400" />
                    <p className="font-semibold">{id}</p>
                  </button>
                ))}
              </div>
            </div>
          )}

          {step === 2 && (
            <div data-testid="onboarding-step-channels">
              <h2 className="font-display text-3xl font-bold mb-2">Canaux prioritaires ?</h2>
              <p className="text-muted-foreground mb-6">Sélectionnez les réseaux publicitaires à cibler.</p>
              <div className="space-y-3">
                {Object.keys(CHANNELS).map((id) => {
                  const c = CHANNELS[id];
                  const active = data.channels.includes(id);
                  return (
                    <button key={id} onClick={() => toggleChannel(id)}
                      className={`w-full p-4 rounded-2xl border flex items-center gap-4 transition-all ${active ? "border-primary bg-primary/10" : "border-border hover:border-primary/40 bg-card"}`}
                      data-testid={`onboarding-channel-${id}`}>
                      <ChannelBadge id={id} />
                      <div className="text-left flex-1">
                        <p className="font-semibold">{c.name}</p>
                        <p className="text-xs text-muted-foreground">{c.sub}</p>
                      </div>
                      {active && <Check size={20} className="text-primary" />}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {step === 3 && (
            <div data-testid="onboarding-step-catalog">
              <h2 className="font-display text-3xl font-bold mb-2">Importez votre catalogue</h2>
              <p className="text-muted-foreground mb-6">Connectez votre boutique (optionnel, vous pourrez le faire plus tard).</p>
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium">Nom de la marque</label>
                  <Input value={data.brand_name} onChange={(e) => setData({ ...data, brand_name: e.target.value })} className="mt-1.5 h-11 rounded-xl" data-testid="onboarding-brand-name" />
                </div>
                <div>
                  <label className="text-sm font-medium flex items-center gap-2"><ShoppingBag size={15} /> URL Shopify / WooCommerce</label>
                  <Input value={data.shopify_url} onChange={(e) => setData({ ...data, shopify_url: e.target.value })} placeholder="https://ma-boutique.myshopify.com" className="mt-1.5 h-11 rounded-xl" data-testid="onboarding-shopify-url" />
                </div>
                <div className="rounded-2xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
                  Glissez un fichier CSV de produits ou connectez Shopify pour synchroniser automatiquement vos produits, prix et stocks.
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-between mt-8">
            <Button variant="ghost" onClick={() => (step === 0 ? navigate("/dashboard") : setStep(step - 1))} data-testid="onboarding-back">
              {step === 0 ? "Passer" : "Retour"}
            </Button>
            {step < 3 ? (
              <Button onClick={next} disabled={!canNext} className="rounded-xl bg-gradient-to-r from-indigo-500 to-cyan-500 text-white border-0 px-8" data-testid="onboarding-next">Continuer</Button>
            ) : (
              <Button onClick={finish} disabled={saving} className="rounded-xl bg-gradient-to-r from-indigo-500 to-cyan-500 text-white border-0 px-8" data-testid="onboarding-finish">
                {saving ? "Enregistrement…" : "Accéder au studio"}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
